import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { savedCompaniesApi } from '../services/prospector/savedCompanies';

const ProspectorLists = () => {
  const navigate = useNavigate();
  const { listId } = useParams(); // Get list ID from URL if available

  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedList, setSelectedList] = useState(null);
  const [showNewListForm, setShowNewListForm] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  // Pagination state for company list
  const [currentPage, setCurrentPage] = useState(1);
  const [companiesPerPage] = useState(20); // Companies per page
  
  // Sorting state
  const [sortBy, setSortBy] = useState('date_added'); // Default sort by date added
  const [sortOrder, setSortOrder] = useState('desc'); // Default sort descending (newest first)
  
  // Search/filter state
  const [searchQuery, setSearchQuery] = useState('');

  // Track current route for restoration
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/prospector')) {
      localStorage.setItem('prospector_last_route', currentPath);
    }
  }, [listId]);

  // Fetch lists on component mount
  useEffect(() => {
    fetchLists();
  }, []);

  // Select the list from URL param if available
  useEffect(() => {
    if (listId && lists.length > 0) {
      console.log('List ID from URL:', listId);
      // Find the list with the matching ID
      const listIdNumber = parseInt(listId, 10);
      const matchedList = lists.find(list => list.id === listIdNumber);

      if (matchedList) {
        console.log('Found matching list:', matchedList);
        handleSelectList(matchedList.id);
      } else {
        console.log('No matching list found for ID:', listId);
      }
    }
  }, [listId, lists]);

  const fetchLists = async () => {
    setLoading(true);
    try {
      const fetchedLists = await savedCompaniesApi.getLists();
      console.log('Fetched lists:', fetchedLists);
      setLists(fetchedLists);
      setError(null);
    } catch (err) {
      console.error('Error fetching lists:', err);
      setError('Failed to load company lists');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateList = async (e) => {
    e.preventDefault();
    
    if (!newListName.trim()) {
      setError('Please enter a name for the list');
      return;
    }
    
    setLoading(true);
    try {
      const newList = await savedCompaniesApi.createList({
        name: newListName,
        description: newListDescription
      });
      
      setLists([...lists, newList]);
      setNewListName('');
      setNewListDescription('');
      setShowNewListForm(false);
      setError(null);
    } catch (err) {
      console.error('Error creating list:', err);
      setError('Failed to create list');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteList = async (listId) => {
    if (window.confirm('Are you sure you want to delete this list?')) {
      try {
        await savedCompaniesApi.deleteList(listId);
        setLists(lists.filter(list => list.id !== listId));
        if (selectedList && selectedList.id === listId) {
          setSelectedList(null);
        }
      } catch (err) {
        console.error('Error deleting list:', err);
        setError('Failed to delete list');
      }
    }
  };

  const handleSelectList = async (listId) => {
    try {
      const list = await savedCompaniesApi.getListById(listId);
      setSelectedList(list);
      setCurrentPage(1); // Reset to first page when selecting a new list
      // Reset sorting to default when selecting a new list
      setSortBy('date_added');
      setSortOrder('desc');
      // Clear search when switching lists
      setSearchQuery('');
      setError(null);
    } catch (err) {
      console.error('Error fetching list details:', err);
      setError('Failed to load list details');
    }
  };

  const handleRemoveCompany = async (listId, companyId) => {
    if (window.confirm('Remove this company from the list?')) {
      try {
        await savedCompaniesApi.removeCompanyFromList(listId, companyId);
        // Update the selected list to reflect the change
        const updatedList = await savedCompaniesApi.getListById(listId);
        setSelectedList(updatedList);
      } catch (err) {
        console.error('Error removing company from list:', err);
        setError('Failed to remove company from list');
      }
    }
  };

  // Handle sorting
  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort column and default to ascending
      setSortBy(columnKey);
      setSortOrder('asc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  // Filter companies based on search query
  const getFilteredCompanies = () => {
    if (!selectedList || !selectedList.companies) return [];
    
    let companies = [...selectedList.companies];
    
    // Apply search filter if there's a search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      companies = companies.filter(company => {
        const companyName = (company.company_name || company.companyName || '').toLowerCase();
        const industryDesc = (company.industry_description || company.industryDescription || '').toLowerCase();
        const industryCode = (company.industry_code || company.industryCode || '').toLowerCase();
        const employeeCount = String(company.antall_ansatte !== undefined ? company.antall_ansatte : 
                                    company.antallAnsatte !== undefined ? company.antallAnsatte : '');
        
        return companyName.includes(query) || 
               industryDesc.includes(query) || 
               industryCode.includes(query) || 
               employeeCount.includes(query);
      });
    }
    
    return companies;
  };

  // Sort companies based on current sort settings
  const getSortedCompanies = () => {
    const filteredCompanies = getFilteredCompanies();
    
    filteredCompanies.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'company_name':
          aValue = (a.company_name || a.companyName || '').toLowerCase();
          bValue = (b.company_name || b.companyName || '').toLowerCase();
          break;
        case 'industry_description':
          aValue = (a.industry_description || a.industryDescription || '').toLowerCase();
          bValue = (b.industry_description || b.industryDescription || '').toLowerCase();
          break;
        case 'employees':
          aValue = a.antall_ansatte !== undefined ? a.antall_ansatte : 
                   a.antallAnsatte !== undefined ? a.antallAnsatte : 0;
          bValue = b.antall_ansatte !== undefined ? b.antall_ansatte : 
                   b.antallAnsatte !== undefined ? b.antallAnsatte : 0;
          // Convert to numbers for proper numeric sorting
          aValue = parseInt(aValue) || 0;
          bValue = parseInt(bValue) || 0;
          break;
        case 'date_added':
        default:
          aValue = new Date(a.date_added || a.dateAdded);
          bValue = new Date(b.date_added || b.dateAdded);
          break;
      }
      
      // Handle string comparisons
      if (typeof aValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      
      // Handle numeric and date comparisons
      if (aValue < bValue) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return filteredCompanies;
  };

  // Pagination calculations
  const getPagedCompanies = () => {
    const sortedCompanies = getSortedCompanies();
    const startIndex = (currentPage - 1) * companiesPerPage;
    const endIndex = startIndex + companiesPerPage;
    return sortedCompanies.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const filteredCompanies = getFilteredCompanies();
    return Math.ceil(filteredCompanies.length / companiesPerPage);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6 flex items-center">
        <button
          onClick={() => {
            console.log('Back to search button clicked');
            // Clear the saved route to prevent redirect loop
            localStorage.removeItem('prospector_last_route');
            navigate('/prospector');
          }}
          className="mr-4 inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
        >
          &larr; Back to search
        </button>
        <h1 className="text-2xl font-bold text-gray-900">My Company Lists</h1>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* List selection dropdown and management */}
      <div className="mb-6">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h2 className="text-lg font-medium text-gray-900">Select List</h2>
                <div className="relative">
                  <select
                    value={selectedList?.id || ''}
                    onChange={(e) => {
                      if (e.target.value) {
                        handleSelectList(parseInt(e.target.value));
                      } else {
                        setSelectedList(null);
                      }
                    }}
                    disabled={loading && lists.length === 0}
                    className="block w-80 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {loading && lists.length === 0 ? 'Loading lists...' : 'Choose a list...'}
                    </option>
                    {lists.map((list) => (
                      <option key={list.id} value={list.id}>
                        {list.name} ({list.company_count || 0} companies)
                      </option>
                    ))}
                  </select>
                  {loading && lists.length === 0 && (
                    <div className="absolute inset-y-0 right-8 flex items-center pointer-events-none">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    </div>
                  )}
                </div>
                {selectedList && (
                  <button
                    onClick={() => handleDeleteList(selectedList.id)}
                    className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none"
                    title="Delete current list"
                  >
                    <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    Delete
                  </button>
                )}
              </div>
              <button
                onClick={() => setShowNewListForm(!showNewListForm)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
              >
                <svg className="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                New List
              </button>
            </div>

            {/* New list form */}
            {showNewListForm && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <form onSubmit={handleCreateList} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                  <div>
                    <label htmlFor="newListName" className="block text-sm font-medium text-gray-700 mb-1">
                      List Name
                    </label>
                    <input
                      type="text"
                      id="newListName"
                      value={newListName}
                      onChange={(e) => setNewListName(e.target.value)}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="e.g. Potential Customers"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="newListDescription" className="block text-sm font-medium text-gray-700 mb-1">
                      Description (optional)
                    </label>
                    <input
                      type="text"
                      id="newListDescription"
                      value={newListDescription}
                      onChange={(e) => setNewListDescription(e.target.value)}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Brief description"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewListForm(false);
                        setNewListName('');
                        setNewListDescription('');
                      }}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                      disabled={loading}
                    >
                      {loading ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Company list - full width */}
      <div>
        {selectedList ? (
          <div className="bg-white shadow-md rounded-lg overflow-hidden flex flex-col h-[calc(100vh-280px)]">
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedList.name}</h2>
                  {selectedList.description && (
                    <p className="mt-1 text-sm text-gray-600">{selectedList.description}</p>
                  )}
                </div>
                <div className="text-sm text-gray-500">
                  {(() => {
                    const totalCompanies = selectedList.companies?.length || 0;
                    const filteredCompanies = getFilteredCompanies().length;
                    
                    if (searchQuery.trim() && filteredCompanies !== totalCompanies) {
                      return `${filteredCompanies} of ${totalCompanies} companies`;
                    }
                    return `${totalCompanies} companies total`;
                  })()}
                </div>
              </div>
              
              {/* Search input */}
              <div className="flex items-center space-x-4">
                <div className="flex-1 max-w-lg">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search companies by name, industry, or employee count..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1); // Reset to first page when searching
                      }}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setCurrentPage(1);
                        }}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

              {selectedList.companies && selectedList.companies.length > 0 ? (
                <>
                  <div className="flex-1 overflow-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th 
                            scope="col" 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort('company_name')}
                          >
                            <div className="flex items-center justify-between">
                              <span>Company</span>
                              <span className={`ml-1 ${sortBy === 'company_name' ? 'text-blue-600' : 'text-gray-300'}`}>
                                {sortBy === 'company_name' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
                              </span>
                            </div>
                          </th>
                          <th 
                            scope="col" 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort('industry_description')}
                          >
                            <div className="flex items-center justify-between">
                              <span>Industry</span>
                              <span className={`ml-1 ${sortBy === 'industry_description' ? 'text-blue-600' : 'text-gray-300'}`}>
                                {sortBy === 'industry_description' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
                              </span>
                            </div>
                          </th>
                          <th 
                            scope="col" 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort('employees')}
                          >
                            <div className="flex items-center justify-between">
                              <span>Employees</span>
                              <span className={`ml-1 ${sortBy === 'employees' ? 'text-blue-600' : 'text-gray-300'}`}>
                                {sortBy === 'employees' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
                              </span>
                            </div>
                          </th>
                          <th 
                            scope="col" 
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort('date_added')}
                          >
                            <div className="flex items-center justify-between">
                              <span>Added</span>
                              <span className={`ml-1 ${sortBy === 'date_added' ? 'text-blue-600' : 'text-gray-300'}`}>
                                {sortBy === 'date_added' ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
                              </span>
                            </div>
                          </th>
                          <th scope="col" className="relative px-6 py-3">
                            <span className="sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {getPagedCompanies().map((company) => (
                          <tr
                            key={company.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              // Set a flag to indicate we're coming from a list view
                              localStorage.setItem('prospector_from_list', 'true');
                              // Make sure to convert to string since localStorage only stores strings
                              localStorage.setItem('prospector_list_id', String(selectedList.id));
                              navigate(`/prospector/${company.organization_number || company.organizationNumber}`);
                            }}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{company.company_name || company.companyName}</div>
                                  <div className="text-sm text-gray-500">{company.organization_number || company.organizationNumber}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{company.industry_code || company.industryCode || '-'}</div>
                              <div className="text-xs text-gray-500 truncate max-w-[200px]">{company.industry_description || company.industryDescription || '-'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {company.antall_ansatte !== undefined ? company.antall_ansatte :
                               company.antallAnsatte !== undefined ? company.antallAnsatte : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(company.date_added || company.dateAdded).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex space-x-2 justify-end">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation(); // Prevent row click event
                                    handleRemoveCompany(selectedList.id, company.id);
                                  }}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  Remove
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Pagination controls - fixed at bottom */}
                  {getTotalPages() > 1 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 flex-shrink-0">
                      <div className="flex-1 flex justify-between sm:hidden">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === getTotalPages()}
                          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm text-gray-700">
                            {(() => {
                              const filteredCount = getFilteredCompanies().length;
                              const startIndex = (currentPage - 1) * companiesPerPage + 1;
                              const endIndex = Math.min(currentPage * companiesPerPage, filteredCount);
                              
                              return (
                                <>
                                  Showing{' '}
                                  <span className="font-medium">{startIndex}</span>
                                  {' '}to{' '}
                                  <span className="font-medium">{endIndex}</span>
                                  {' '}of{' '}
                                  <span className="font-medium">{filteredCount}</span>
                                  {' '}companies
                                  {searchQuery.trim() && filteredCount !== selectedList.companies.length && (
                                    <span className="text-gray-500"> (filtered from {selectedList.companies.length})</span>
                                  )}
                                </>
                              );
                            })()}
                          </p>
                        </div>
                        <div>
                          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                            <button
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span className="sr-only">Previous</span>
                              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </button>
                            
                            {/* Page numbers */}
                            {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map((page) => {
                              const isCurrentPage = page === currentPage;
                              const showPage = page === 1 || page === getTotalPages() || 
                                             (page >= currentPage - 1 && page <= currentPage + 1);
                              
                              if (!showPage && page !== currentPage - 2 && page !== currentPage + 2) {
                                return null;
                              }
                              
                              if ((page === currentPage - 2 && currentPage > 3) || 
                                  (page === currentPage + 2 && currentPage < getTotalPages() - 2)) {
                                return (
                                  <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                    ...
                                  </span>
                                );
                              }
                              
                              return (
                                <button
                                  key={page}
                                  onClick={() => handlePageChange(page)}
                                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                    isCurrentPage
                                      ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  {page}
                                </button>
                              );
                            })}
                            
                            <button
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === getTotalPages()}
                              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <span className="sr-only">Next</span>
                              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </nav>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center p-12">
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No companies</h3>
                    <p className="mt-1 text-sm text-gray-500">This list doesn't have any companies yet.</p>
                    <div className="mt-6">
                      <button
                        type="button"
                        onClick={() => navigate('/prospector')}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                      >
                        <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M10 3a7 7 0 100 14 7 7 0 000-14zm1 9v-2h2a1 1 0 110 2h-2zm-1 2a1 1 0 01-1-1v-2H7a1 1 0 110-2h2V7a1 1 0 112 0v2h2a1 1 0 110 2h-2v2a1 1 0 01-1 1z" clipRule="evenodd" />
                        </svg>
                        Search Companies
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
        ) : (
          <div className="bg-white shadow-md rounded-lg p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Select a list</h3>
            <p className="mt-1 text-sm text-gray-500">
              Choose a list from the dropdown above or create a new one.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProspectorLists;